import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import { useAccount } from '@/hooks/useAccount';
import { addDaysToDate, formatDate } from '@/util/dateUtils';

const CONVERSATION_SEARCH_QUERY = gql`
  query SearchConversations(
    $accountId: uuid
    $query: String
    $startDate: timestamp
    $endDate: timestamp
  ) {
    rows: search_sessions(
      args: { account_id: $accountId, query: $query, start_time: $startDate, end_time: $endDate }
      limit: 5
      order_by: { start_time: desc }
    ) {
      session_id
      user_name
      user_email
      channel_user_id
      start_time
      end_time
      channel
      desk_id
      total_user_messages
    }
  }
`;

interface ConversationSearchRow {
  session_id: string;
  user_name: string | null;
  user_email: string | null;
  channel_user_id: string | null;
  start_time: string;
  end_time: string;
  channel: string;
  desk_id: string;
  total_user_messages: number;
}

export interface ConversationSearchResult {
  userName: string | null;
  userEmail: string | null;
  channelUserId: string | null;
  sessionId: string;
  deskId: string;
  channel: string;
  startTime: string;
  totalUserMessages: number;
}

const DEFAULT_RETENTION_DAYS = 30;
export const MIN_QUERY_LENGTH = 3;

export const getDateRange = (maxRetention: number | undefined) => {
  const endDate = new Date();
  const startDate = addDaysToDate(endDate, -(maxRetention || DEFAULT_RETENTION_DAYS));
  return {
    startDate: formatDate(startDate ?? endDate, 'yyyy-MM-dd'),
    endDate: formatDate(endDate, 'yyyy-MM-dd'),
  };
};

/**
 * Reactive hook to search conversations by name, phone, or email.
 * Fires automatically when debouncedQuery has >= 3 characters.
 */
export const useConversationSearch = (debouncedQuery: string) => {
  const { account } = useAccount();

  const trimmed = debouncedQuery.trim();
  const skip = !account?.account_id || trimmed.length < MIN_QUERY_LENGTH;

  const dateRange = useMemo(
    () => getDateRange(account?.max_log_retention),
    [account?.max_log_retention]
  );

  const { data, loading } = useQuery<{ rows: ConversationSearchRow[] }>(CONVERSATION_SEARCH_QUERY, {
    variables: { accountId: account?.account_id, query: trimmed, ...dateRange },
    skip,
    fetchPolicy: 'network-only',
  });

  const results = useMemo<ConversationSearchResult[]>(
    () =>
      data?.rows.map((row) => ({
        userName: row.user_name,
        userEmail: row.user_email,
        channelUserId: row.channel_user_id,
        sessionId: row.session_id,
        deskId: row.desk_id,
        channel: row.channel,
        startTime: row.start_time,
        totalUserMessages: row.total_user_messages,
      })) ?? [],
    [data]
  );

  return { results, isSearching: loading && !skip };
};
